import { FC, useCallback } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { trackEvent } from 'dibs-tracking';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';
import { PdpRespStorytellingText_storyTellingTab } from './__generated__/PdpRespStorytellingText_storyTellingTab.graphql';
import { fireReadMoreTracking } from '../PdpSharedStorytellingComponents/pdpSharedStorytellingHelpers';
import styles from '../PdpSharedStorytellingComponents/PdpSharedStorytellingText.scss';

type Props = {
    storyTellingTab: PdpRespStorytellingText_storyTellingTab;
};

const PdpRespStorytellingTextComponent: FC<Props> = ({ storyTellingTab }) => {
    const {
        tabTitle,
        contentHeader,
        contentCreatorAttribution,
        contentBodyText,
        tabType,
    } = storyTellingTab;

    const readMoreText = (
        <FormattedMessage defaultMessage="Read more" id="pdp.resp.storytelling.readMoreLink" />
    );

    const onReadMoreClick = useCallback(() => {
        fireReadMoreTracking({ tabType });
    }, [tabType]);

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    <Heading className={styles.tabTitle}>{tabTitle}</Heading>
                    {contentHeader && (
                        <div className={styles.contentHeaderWrapper}>
                            <Heading
                                className={styles.contentHeader}
                                dangerouslySetInnerHTML={{ __html: contentHeader }}
                            />
                        </div>
                    )}
                    {contentCreatorAttribution && (
                        <div className={styles.creatorAttribution}>
                            <FormattedMessage
                                defaultMessage="Designed by {creator}"
                                id="pdp.resp.storytelling.creatorAttribution"
                                values={{
                                    creator: (
                                        <SeoLink
                                            linkData={contentCreatorAttribution.creatorBrowseUrl}
                                            dataTn="pdp-storytelling-design-attribution-link"
                                            onClick={() => {
                                                trackEvent({
                                                    category: 'promo interaction',
                                                    action: 'iconic design creator clicked',
                                                    label: 'pdp',
                                                });
                                            }}
                                        >
                                            {contentCreatorAttribution?.creatorName}
                                        </SeoLink>
                                    ),
                                }}
                            />
                        </div>
                    )}
                    <ExpandingAreaWithReadMore
                        lineClamp={5}
                        showGradient={false}
                        collapsedHeight={120}
                        readMore={readMoreText}
                        onReadMoreClick={onReadMoreClick}
                        wrapperClass={styles.contentBodyWrapper}
                    >
                        <div
                            className={styles.contentBodyText}
                            dangerouslySetInnerHTML={{ __html: contentBodyText }}
                        />
                    </ExpandingAreaWithReadMore>
                </>
            )}
        </HeadingLevel>
    );
};

export const PdpRespStorytellingText = createFragmentContainer(PdpRespStorytellingTextComponent, {
    storyTellingTab: graphql`
        fragment PdpRespStorytellingText_storyTellingTab on StoryTellingTab {
            tabTitle
            contentHeader
            contentCreatorAttribution {
                creatorName
                creatorBrowseUrl {
                    ...SeoLink_linkData
                }
            }
            contentBodyText

            tabType
        }
    `,
});
